import React, { useEffect, useState } from 'react';
import {
  Button,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  Popconfirm,
} from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Notice } from '../common/Notice';
import {
  QuestionGroupDetail,
  QuestionGroupCreatePayload,
  QuestionGroupUpdatePayload,
  GroupGeographicScope,
} from '../../types/survey-question-group.types';
import { surveyQuestionGroupService } from '../../services/survey-question-group.service';
import { profileService } from '../../services/profile.service';

const { Text } = Typography;

interface State {
  id: string;
  name: string;
  code: string;
}

interface QuestionGroupEditorProps {
  surveyId: string;
  surveyScope: string;
  isAdmin: boolean;
}

export const QuestionGroupEditor: React.FC<QuestionGroupEditorProps> = ({
  surveyId,
  surveyScope,
  isAdmin,
}) => {
  const [groups, setGroups] = useState<QuestionGroupDetail[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<QuestionGroupDetail | null>(null);
  const [form] = Form.useForm();

  const listGroups = async () => {
    setLoading(true);
    try {
      const items = isAdmin
        ? await surveyQuestionGroupService.listGroupsAdmin(surveyId)
        : await surveyQuestionGroupService.listGroupsContractor(surveyId);
      setGroups(items);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    listGroups();
    loadStates();
  }, [surveyId]);

  const loadStates = async () => {
    try {
      const data = await profileService.getStates();
      setStates(data.map((s: any) => ({ id: s.id, name: s.name, code: s.code })));
    } catch {
      // states load is best-effort
    }
  };

  const locked = groups.some((g) => g.locked);

  const openCreate = () => {
    setEditingGroup(null);
    form.resetFields();
    form.setFieldsValue({ showNameToParticipant: false, scope: 'NATIONAL' });
    setModalOpen(true);
  };

  const openEdit = (group: QuestionGroupDetail) => {
    setEditingGroup(group);
    form.setFieldsValue({
      name: group.name,
      showNameToParticipant: group.showNameToParticipant,
      order: group.order,
      scope: group.scope,
      stateId: group.stateId ?? undefined,
    });
    setModalOpen(true);
  };

  const handleDelete = async (groupId: string) => {
    setLoading(true);
    try {
      if (isAdmin) {
        await surveyQuestionGroupService.deleteGroupAdmin(surveyId, groupId);
      } else {
        await surveyQuestionGroupService.deleteGroupContractor(surveyId, groupId);
      }
      await listGroups();
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    setLoading(true);
    try {
      if (editingGroup) {
        const payload: QuestionGroupUpdatePayload = {
          name: values.name,
          showNameToParticipant: values.showNameToParticipant,
          order: values.order,
          scope: values.scope as GroupGeographicScope,
          stateId: values.scope === 'STATE' ? values.stateId : null,
        };
        if (isAdmin) {
          await surveyQuestionGroupService.updateGroupAdmin(surveyId, editingGroup.id, payload);
        } else {
          await surveyQuestionGroupService.updateGroupContractor(surveyId, editingGroup.id, payload);
        }
      } else {
        const payload: QuestionGroupCreatePayload = {
          name: values.name,
          showNameToParticipant: values.showNameToParticipant ?? false,
          order: values.order,
          scope: values.scope as GroupGeographicScope,
          stateId: values.scope === 'STATE' ? values.stateId : null,
        };
        if (isAdmin) {
          await surveyQuestionGroupService.createGroupAdmin(surveyId, payload);
        } else {
          await surveyQuestionGroupService.createGroupContractor(surveyId, payload);
        }
      }
      setModalOpen(false);
      await listGroups();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Erro ao salvar grupo';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const scopeOptions = [
    { label: 'Nacional (todos os participantes)', value: 'NATIONAL' },
    ...(surveyScope !== 'CITY'
      ? [{ label: 'Estadual (somente participantes do estado)', value: 'STATE' }]
      : []),
  ];

  const columns = [
    {
      title: 'Ordem',
      dataIndex: 'order',
      width: 70,
      render: (v: number) => <Text>{v}</Text>,
    },
    {
      title: 'Nome',
      dataIndex: 'name',
      render: (name: string, record: QuestionGroupDetail) => (
        <Space>
          {name}
          {record.locked && <Tag color="orange">Bloqueado</Tag>}
        </Space>
      ),
    },
    {
      title: 'Abrangência',
      dataIndex: 'scope',
      render: (scope: GroupGeographicScope, record: QuestionGroupDetail) =>
        scope === 'NATIONAL' ? (
          <Tag color="blue">Nacional</Tag>
        ) : (
          <Tag color="purple">{record.stateName ?? `Estado ${record.stateId}`}</Tag>
        ),
    },
    {
      title: 'Nome visível',
      dataIndex: 'showNameToParticipant',
      render: (v: boolean) => (v ? 'Sim' : 'Não'),
    },
    {
      title: 'Ações',
      render: (_: unknown, record: QuestionGroupDetail) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            disabled={locked}
            onClick={() => openEdit(record)}
          >
            Editar
          </Button>
          <Popconfirm
            title="Remover grupo?"
            description="As perguntas do grupo perderão a associação."
            onConfirm={() => handleDelete(record.id)}
            disabled={locked}
          >
            <Button type="link" danger icon={<DeleteOutlined />} disabled={locked}>
              Remover
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {locked && (
        <Notice
          type="warning"
          showIcon
          message="Pesquisa lançada — estrutura de grupos bloqueada"
          description="Não é possível adicionar, remover ou reordenar grupos após o lançamento da pesquisa."
          style={{ marginBottom: 16 }}
        />
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openCreate}
          disabled={locked}
        >
          Adicionar Grupo
        </Button>
      </div>

      <Table
        rowKey="id"
        dataSource={groups}
        columns={columns}
        loading={loading}
        pagination={false}
        size="small"
      />

      <Modal
        open={modalOpen}
        title={editingGroup ? 'Editar Grupo' : 'Novo Grupo de Perguntas'}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        confirmLoading={loading}
        okText={editingGroup ? 'Salvar' : 'Criar'}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Nome interno do grupo"
            rules={[{ required: true, message: 'Informe o nome do grupo' }]}
          >
            <Input maxLength={150} placeholder="Ex: Bloco Nacional, Bloco São Paulo" />
          </Form.Item>

          <Form.Item name="showNameToParticipant" label="Exibir nome ao participante" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item
            name="order"
            label="Posição na pesquisa"
            rules={[{ required: true, message: 'Informe a posição' }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="scope"
            label="Abrangência"
            rules={[{ required: true, message: 'Selecione a abrangência' }]}
          >
            <Select
              options={scopeOptions}
              onChange={() => form.setFieldValue('stateId', undefined)}
            />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.scope !== curr.scope}
          >
            {({ getFieldValue }) =>
              getFieldValue('scope') === 'STATE' ? (
                <Form.Item
                  name="stateId"
                  label="Estado"
                  rules={[{ required: true, message: 'Selecione o estado' }]}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    options={states.map((s) => ({ label: `${s.code} — ${s.name}`, value: s.id }))}
                    placeholder="Selecione o estado"
                  />
                </Form.Item>
              ) : null
            }
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
